import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, Thermometer, ShieldAlert, TrendingUp, MessageSquareWarning, Check, Sparkles, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";

const REFRESH_MS = 60_000;

const SEV_STYLES = {
  critical: "bg-red-600 text-white",
  high: "bg-red-100 text-red-800",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-blue-100 text-blue-800",
  unknown: "bg-slate-100 text-slate-700",
};

const SRC_ICONS = {
  env_fail: Thermometer,
  hold: ShieldAlert,
  ccp_fail: AlertTriangle,
  high_risk_batch: TrendingUp,
  complaint_cluster: MessageSquareWarning,
};

const SRC_LABELS = {
  env_fail: "Env Fail",
  hold: "Batch Hold",
  ccp_fail: "CCP Fail",
  high_risk_batch: "High-Risk Batch",
  complaint_cluster: "Complaint Spike",
};

function timeAgo(iso) {
  try {
    const then = new Date(iso).getTime();
    const diff = (Date.now() - then) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

export function QualityAlertsBell() {
  const [data, setData] = useState({ alerts: [], unread: 0, total: 0, by_severity: {} });
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/qcai/alerts");
      setData(r.data);
    } catch (e) {
      // Silent — user may not have qc permission; hide bell in that case
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return data.alerts || [];
    if (filter === "unread") return (data.alerts || []).filter((a) => !a.read);
    return (data.alerts || []).filter((a) => a.source === filter);
  }, [data, filter]);

  const markRead = async (ids) => {
    if (!ids?.length) return;
    try {
      await api.post("/qcai/alerts/mark-read", { alert_ids: ids });
      // Optimistic — mark locally instead of full refetch
      setData((prev) => ({
        ...prev,
        alerts: (prev.alerts || []).map((a) => (ids.includes(a.id) ? { ...a, read: true } : a)),
        unread: Math.max(0, (prev.unread || 0) - ids.length),
      }));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      const r = await api.post("/qcai/alerts/mark-all-read");
      toast.success(`Marked ${r.data?.marked || 0} alert(s) read`);
      setData((prev) => ({
        ...prev,
        alerts: (prev.alerts || []).map((a) => ({ ...a, read: true })),
        unread: 0,
      }));
    } catch (e) { toast.error("Failed to mark all read"); }
    finally { setBusy(false); }
  };

  const openAlert = (a) => {
    if (!a.read) markRead([a.id]);
    setOpen(false);
    // Route based on module — currently always qc, with tab hint
    const tab = a.link_tab || "";
    navigate(`/app/qc${tab ? `?tab=${tab}` : ""}`);
  };

  const badge = data.unread > 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="qc-alerts-bell"
          className="relative w-9 h-9 rounded-md border border-[#EAE5D9] flex items-center justify-center hover:border-[#C05621] transition-colors"
          title="Quality alerts"
        >
          <Bell className={`w-4 h-4 ${badge ? "text-red-600" : "text-[#5C4033]"}`} />
          {badge && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="qc-alerts-unread-count">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md bg-white flex flex-col p-0" data-testid="qc-alerts-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#EAE5D9]">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-[Outfit] text-lg flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-700" /> Quality Alerts</SheetTitle>
            <Button size="sm" variant="outline" onClick={markAllRead} disabled={busy || !data.unread} data-testid="qc-alerts-mark-all">
              <Check className="w-3 h-3 mr-1" /> Mark all read
            </Button>
          </div>
          <SheetDescription className="sr-only">Real-time quality alerts across env fails, batch holds, CCP fails, high-risk batches and complaint spikes. Auto-refresh every 60 seconds.</SheetDescription>
          <div className="flex items-center gap-2 text-xs text-[#8B7355] mt-2 flex-wrap">
            <span data-testid="qc-alerts-total">{data.total} total</span>
            <span>·</span>
            <span data-testid="qc-alerts-unread">{data.unread} unread</span>
            {Object.entries(data.by_severity || {}).map(([sev, count]) => (
              <Badge key={sev} className={`border-0 text-[10px] ${SEV_STYLES[sev] || SEV_STYLES.unknown}`}>{sev} {count}</Badge>
            ))}
          </div>
          <div className="flex gap-1 mt-2 overflow-x-auto pb-1" data-testid="qc-alerts-filters">
            {["all", "unread", "env_fail", "hold", "ccp_fail", "high_risk_batch", "complaint_cluster"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`qc-alerts-filter-${f}`}
                className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap transition-colors ${filter === f ? "bg-[#2C1810] text-white" : "bg-[#FAF6EE] text-[#8B7355] hover:bg-[#EAE5D9]"}`}
              >
                {f === "all" ? "All" : f === "unread" ? "Unread" : SRC_LABELS[f]}
              </button>
            ))}
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto" data-testid="qc-alerts-list">
          {filtered.length === 0 && (
            <div className="text-center py-16 text-[#8B7355]">
              <Bell className="w-8 h-8 mx-auto mb-3 text-[#EAE5D9]" />
              <div className="text-sm">
                {filter === "unread" ? "All caught up!" : "No alerts in the last 24 hours."}
              </div>
            </div>
          )}
          {filtered.map((a) => {
            const Icon = SRC_ICONS[a.source] || Bell;
            return (
              <div
                key={a.id}
                onClick={() => openAlert(a)}
                data-testid={`qc-alert-item-${a.source}`}
                className={`px-5 py-3 border-b border-[#EAE5D9] cursor-pointer hover:bg-[#FAF6EE] transition-colors flex gap-3 ${a.read ? "opacity-60" : ""}`}
              >
                <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${SEV_STYLES[a.severity] || SEV_STYLES.unknown}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <div className="text-sm font-semibold text-[#2C1810] flex-1 leading-tight">{a.title}</div>
                    {!a.read && <span className="w-2 h-2 rounded-full bg-red-600 mt-1.5 flex-shrink-0" />}
                  </div>
                  <div className="text-xs text-[#8B7355] mt-0.5 line-clamp-2">{a.subtitle}</div>
                  <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                    <Badge className="bg-slate-100 text-slate-700 border-0">{SRC_LABELS[a.source] || a.source}</Badge>
                    <span className="text-[#8B7355]">{timeAgo(a.at)}</span>
                    <ExternalLink className="w-3 h-3 text-[#8B7355] ml-auto" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t border-[#EAE5D9] px-5 py-2 text-[10px] text-[#8B7355] text-center">
          Auto-refresh every {REFRESH_MS / 1000}s · Last update {new Date(data.generated_at || Date.now()).toLocaleTimeString("en-IN")}
        </div>
      </SheetContent>
    </Sheet>
  );
}
